import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDatabase } from '../../hooks/useDatabase';
// import { Contact, Organization } from '../../types/database';

interface CsvRow {
  email: string;
  name: string;
  organization: string;
  description: string;
  phone: string;
  industry: string;
}

interface UploadResult {
  success: boolean;
  message: string;
  data?: any;
}

interface CsvUploadProps {
  onUploadComplete?: () => void;
}

export function CsvUpload({ onUploadComplete }: CsvUploadProps) {
  const db = useDatabase();
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseCsv = useCallback((csvText: string): CsvRow[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    // Parse header
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredHeaders = ['email', 'name', 'organization', 'description', 'phone', 'industry'];
    
    // Check if all required headers are present
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
    }

    // Parse data rows
    const data: CsvRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) continue;

      const row: CsvRow = {
        email: values[headers.indexOf('email')] || '',
        name: values[headers.indexOf('name')] || '',
        organization: values[headers.indexOf('organization')] || '',
        description: values[headers.indexOf('description')] || '',
        phone: values[headers.indexOf('phone')] || '',
        industry: values[headers.indexOf('industry')] || '',
      };

      // Skip empty rows
      if (row.email || row.name) {
        data.push(row);
      }
    }

    return data;
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResults([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const parsedData = parseCsv(csvText);
        setCsvData(parsedData);
        
        if (parsedData.length === 0) {
          setError('No valid data found in CSV file');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse CSV file');
        setCsvData([]);
      }
    };
    reader.readAsText(selectedFile);
  }, [parseCsv]);

  const findOrCreateOrganization = useCallback(async (orgName: string): Promise<string | null> => {
    if (!orgName.trim()) return null;

    try {
      // First, try to find existing organization
      const { data: existingOrgs } = await db.getOrganizations();
      const existingOrg = existingOrgs?.find(org => 
        org.name.toLowerCase() === orgName.toLowerCase()
      );

      if (existingOrg) {
        return existingOrg.id;
      }

      // Create new organization
      const { data: newOrg, error } = await db.createOrganization({
        name: orgName,
        description: `Organization imported from CSV`,
        industry: '', // Will be updated if we have industry data
        user_id: '', // This will be set by the database hook
      });

      if (error) {
        console.error('Error creating organization:', error);
        return null;
      }

      return newOrg?.id || null;
    } catch (err) {
      console.error('Error in findOrCreateOrganization:', err);
      return null;
    }
  }, [db]);

  const uploadContacts = useCallback(async () => {
    if (csvData.length === 0) return;

    setLoading(true);
    setUploadProgress(0);
    setResults([]);
    setError(null);

    const newResults: UploadResult[] = [];
    const totalRows = csvData.length;

    try {
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];
        setUploadProgress((i / totalRows) * 100);

        try {
          // Find or create organization
          let organizationId: string | null = null;
          if (row.organization.trim()) {
            organizationId = await findOrCreateOrganization(row.organization);
          }

          // Create contact
          const contactData = {
            name: row.name,
            email: row.email,
            phone: row.phone || undefined,
            description: row.description || undefined,
            industry: row.industry || undefined,
            // organization_id removed - organizations managed via junction table
            user_id: '', // This will be set by the database hook
            tags: {},
            associated_contacts: [],
            relationship_types: {},
            industries: [],
            skills: [],
            interests: [],
          };

          const { data: contact, error } = await db.createContact(contactData);

          if (error) {
            newResults.push({
              success: false,
              message: `Failed to create contact ${row.name}: ${error.message}`,
            });
          } else {
            // Link organization if one was found/created
            if (contact && organizationId) {
              const { error: linkError } = await db.linkContactToOrganization(contact.id, organizationId);
              if (linkError) {
                console.error('Error linking organization:', linkError);
                // Don't fail the whole operation, just log the error
              }
            }
            
            newResults.push({
              success: true,
              message: `Successfully created contact ${row.name}`,
              data: contact,
            });
          }
        } catch (err) {
          newResults.push({
            success: false,
            message: `Error processing row ${i + 1}: ${err instanceof Error ? err.message : 'Unknown error'}`,
          });
        }
      }

      setUploadProgress(100);
      setResults(newResults);
      
      // Call completion callback
      if (onUploadComplete) {
        onUploadComplete();
      }
      
      // Trigger networking suggestions recalculation
      try {
        // Debounce the recalculation to avoid multiple calls
        setTimeout(async () => {
          await db.recalculateNetworkingSuggestions();
        }, 1000);
        console.log(`CSV upload completed. ${newResults.filter(r => r.success).length} contacts created. Networking suggestions recalculation triggered.`);
      } catch (err) {
        console.error('Error triggering networking recalculation:', err);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLoading(false);
    }
  }, [csvData, db, findOrCreateOrganization, onUploadComplete]);

  const resetUpload = useCallback(() => {
    setFile(null);
    setCsvData([]);
    setResults([]);
    setError(null);
    setUploadProgress(0);
  }, []);

  const successCount = results.filter(r => r.success).length;
  const errorCount = results.filter(r => !r.success).length;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Import Contacts from CSV
        </Typography>
        {file && (
          <Tooltip title="Reset upload">
            <IconButton onClick={resetUpload} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload a CSV file with the following columns: email, name, organization, description, phone, industry
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          href="/sample_contacts.csv"
          download="sample_contacts.csv"
          sx={{ mr: 2 }}
        >
          Download Sample CSV
        </Button>
        <Typography variant="caption" color="text.secondary">
          Use this template to ensure proper formatting
        </Typography>
      </Box>

      {!file ? (
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="csv-upload-input"
          />
          <label htmlFor="csv-upload-input">
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Click to upload CSV file
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or drag and drop your CSV file here
            </Typography>
          </label>
        </Box>
      ) : (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon />
              <Typography variant="body2">
                File: {file.name} ({csvData.length} rows)
              </Typography>
            </Box>
          </Alert>

          {csvData.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setPreviewOpen(true)}
                sx={{ mr: 2 }}
              >
                Preview Data
              </Button>
              <Button
                variant="contained"
                onClick={uploadContacts}
                disabled={loading}
                startIcon={loading ? <LinearProgress sx={{ width: 20, height: 20 }} /> : <UploadIcon />}
              >
                {loading ? 'Uploading...' : 'Upload Contacts'}
              </Button>
            </Box>
          )}

          {loading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Math.round(uploadProgress)}% complete
              </Typography>
            </Box>
          )}

          {results.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Upload Results
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip
                  icon={<SuccessIcon />}
                  label={`${successCount} successful`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<ErrorIcon />}
                  label={`${errorCount} failed`}
                  color="error"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {results.map((result, index) => (
                  <Alert
                    key={index}
                    severity={result.success ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  >
                    {result.message}
                  </Alert>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          CSV Data Preview
          <Typography variant="body2" color="text.secondary">
            {csvData.length} rows ready to import
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Industry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvData.slice(0, 10).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.organization}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>{row.industry}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {csvData.length > 10 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Showing first 10 rows of {csvData.length} total rows
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
